import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import { Play } from "lucide-react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { getUID, sendNotfication, API } from '../../../../../Helper';
import CASINO_GAMES_CONFIG from '../../../../../config/casinoGamesConfig';

const HUIDU_BASE = `${API}/huiduapi`;

// ── Styled ──────────────────────────────────────────────────
const GameOverlay = styled.div`
  position:absolute;inset:0;
  background:rgba(0,0,0,.4);backdrop-filter:blur(4px);
  border-radius:8px;display:flex;justify-content:center;align-items:center;
  opacity:0;transition:opacity .2s ease;
`;
const GameCard = styled.div`
  min-width:142px;height:195px;border-radius:8px;
  position:relative;cursor:pointer;overflow:hidden;
  transition:transform .2s ease;background:#1e2223;
  &:hover{transform:translateY(-5px);}
  &:hover ${GameOverlay}{opacity:1;}
  @media(max-width:768px){min-width:115px;height:160px;}
`;
const GameImage = styled.img`
  width:100%;height:100%;object-fit:cover;border-radius:8px;display:block;
`;
const GameName = styled.div`
  position:absolute;bottom:0;left:0;right:0;
  background:linear-gradient(transparent,rgba(0,0,0,.8));
  color:#fff;font-size:11px;font-weight:600;padding:18px 6px 6px;
  border-radius:0 0 8px 8px;text-align:center;
`;
const PlayCircle = styled.div`
  width:50px;height:50px;border-radius:50%;
  background:#25EE89;display:flex;
  justify-content:center;align-items:center;
`;
const GridWrapper = styled.div`
  display:grid;
  grid-template-columns:repeat(auto-fill,minmax(142px,1fr));
  gap:16px;padding:0 10px;
  @media(max-width:768px){grid-template-columns:repeat(auto-fill,minmax(115px,1fr));}
`;
const HeaderContainer = styled.div`
  display:flex;justify-content:space-between;align-items:center;
  margin-bottom:16px;padding:0 10px;
`;
const TopTitle = styled.h3`color:#fff;font-size:18px;font-weight:700;margin:0;`;
const ProviderBadge = styled.div`
  background:#2e3436;color:#aaa;font-size:12px;
  padding:4px 10px;border-radius:6px;
`;
const Skel = styled.div`
  width:100%;height:100%;border-radius:8px;
  background:linear-gradient(90deg,rgba(190,190,190,.15) 25%,rgba(129,129,129,.22) 37%,rgba(190,190,190,.15) 63%);
  background-size:400% 100%;
  animation:sk 1.4s ease infinite;
  @keyframes sk{0%{background-position:100% 50%}100%{background-position:0 50%}}
`;
const FullScreenContainer = styled.div`
  position:fixed;inset:0;background:#292d2e;z-index:100001;
  display:flex;justify-content:center;align-items:center;
`;
const FullScreenIframe = styled.iframe`width:100%;height:100%;border:none;`;
const BackButton = styled.button`
  position:absolute;top:20px;left:20px;
  background:rgba(0,0,0,.5);color:#fff;border:none;
  border-radius:50%;width:40px;height:40px;
  display:flex;justify-content:center;align-items:center;
  cursor:pointer;z-index:10000;
  &:hover{background:rgba(0,0,0,.8);}
`;
const EmptyState = styled.div`
  width:100%;text-align:center;padding:60px 20px;color:#888;font-size:15px;
`;

// ── URL slug → config code mapping ──────────────────────────
// Maps the URL path (/providers/evolution) to casinoGamesConfig code
const SLUG_MAP = {
  'evolution':        'EVOASIA',
  'evoasia':          'EVOASIA',
  'ezugi':            'EZUGI',
  'pragmaticplay':    'PP',
  'pragmatic-play':   'PP',
  'pp':               'PP',
  'pragmaticlive':    'PPLIVE',
  'pragmatic-live':   'PPLIVE',
  'pplive':           'PPLIVE',
  'pgsoft':           'PG',
  'pg':               'PG',
  'jili':             'JL',
  'jiligames':        'JL',
  'jl':               'JL',
  'spribe':           'SPRIBE',
  'jdb':              'JDB',
  'netent':           'NETENTASIA',
  'netentasia':       'NETENTASIA',
  'funkygames':       'FUNKYGAMES',
  'funky-games':      'FUNKYGAMES',
  'va':               'VA',
  'askmeslot':        'ASKMESLOT',
  'playtech':         'PTASIA',
  'ptasia':           'PTASIA',
  'hacksawworld':     'HACKSAWWORLD',
  'hacksaw':          'HACKSAWWORLD',
  'bng':              'BNG',
  'rich88':           'Rich88',
  'pix':              'PIX',
  'nextspin':         'NS',
  'ns':               'NS',
  'v8':               'V8',
  'evoplay':          'EVOPLAYASIA',
  'evoplayasia':      'EVOPLAYASIA',
  'onlyplay':         'ONLYPLAY',
};

function resolveProvider(slug) {
  const key = (slug || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  // Try exact match first
  if (SLUG_MAP[slug.toLowerCase()]) return SLUG_MAP[slug.toLowerCase()];
  // Try cleaned key
  for (const [k, v] of Object.entries(SLUG_MAP)) {
    if (k.replace(/[^a-z0-9]/g, '') === key) return v;
  }
  // Fallback: match by name in config (case-insensitive)
  const found = CASINO_GAMES_CONFIG.find(p =>
    p.name.toLowerCase().replace(/\s/g, '') === key ||
    p.code.toLowerCase() === key
  );
  return found ? found.code : null;
}

class GameByProvider extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showIframe: false,
      iframeUrl: '',
      iframeLoading: false,
      launching: false,
    };
  }

  getProviderConfig() {
    const { match } = this.props;
    const slug = match?.params?.provider || '';
    const code = resolveProvider(slug);
    return CASINO_GAMES_CONFIG.find(p => p.code === code) || null;
  }

  handleLaunch = async (game) => {
    const isLogged = localStorage.getItem('logged') === 'true';
    if (!isLogged) { sendNotfication('Please login to play', 'error'); return; }

    this.setState({ launching: true });
    try {
      const uid = getUID();
      const currency = localStorage.getItem('coin') || 'PKR';
      const res = await fetch(`${HUIDU_BASE}/game/play`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          game_uid: game.game_uid,
          user_id: uid,
          currency_code: currency,
          supplier_code: game.supplier || '',
          language: 'en',
        }),
      });
      const json = await res.json();
      if (json.success && json.data?.game_launch_url) {
        this.setState({ iframeUrl: json.data.game_launch_url, showIframe: true, iframeLoading: true });
      } else {
        sendNotfication(json.error || 'Failed to launch game', 'error');
      }
    } catch (err) {
      sendNotfication(err.message || 'Unexpected error', 'error');
    } finally {
      this.setState({ launching: false });
    }
  };

  render() {
    const { showIframe, iframeUrl, iframeLoading, launching } = this.state;
    const provider = this.getProviderConfig();
    const { match } = this.props;
    const slug = match?.params?.provider || '';

    if (showIframe) {
      return (
        <FullScreenContainer>
          {iframeLoading && (
            <img src="/assets/images/lg.png" alt="Loading"
              style={{ position:'absolute',top:'50%',left:'50%',
                transform:'translate(-50%,-50%)',width:220,animation:'fadeinout 2s infinite' }} />
          )}
          <FullScreenIframe
            src={iframeUrl}
            onLoad={() => this.setState({ iframeLoading: false })}
            allowFullScreen
          />
          <BackButton onClick={() => this.setState({ showIframe: false, iframeUrl: '', iframeLoading: false })}>
            <FontAwesomeIcon icon={faArrowLeft} />
          </BackButton>
        </FullScreenContainer>
      );
    }

    if (!provider) {
      return (
        <div style={{ padding: 20 }}>
          <HeaderContainer>
            <TopTitle>{slug.toUpperCase()}</TopTitle>
          </HeaderContainer>
          <EmptyState>No games found for this provider.</EmptyState>
        </div>
      );
    }

    const games = provider.games || [];

    return (
      <div style={{ padding: '10px 0' }}>
        {launching && (
          <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,.55)',
            zIndex:998,display:'flex',alignItems:'center',justifyContent:'center' }}>
            <div style={{ color:'#fff',fontSize:15 }}>Launching game…</div>
          </div>
        )}

        <HeaderContainer>
          <TopTitle>{provider.name}</TopTitle>
          <ProviderBadge>{games.length} Games</ProviderBadge>
        </HeaderContainer>

        <GridWrapper>
          {games.length === 0
            ? Array(8).fill(null).map((_, i) => (
                <GameCard key={i}><Skel /></GameCard>
              ))
            : games.map((game, idx) => (
                <GameCard
                  key={game.game_uid || idx}
                  onClick={() => this.handleLaunch(game)}
                >
                  <GameImage
                    src={game.image || '/assets/images/default_card3.png'}
                    alt={game.game_name}
                    loading="lazy"
                    onError={e => { e.target.src = '/assets/images/default_card3.png'; }}
                  />
                  <GameName>{game.game_name}</GameName>
                  <GameOverlay>
                    <PlayCircle>
                      <Play size={24} color="#fff" />
                    </PlayCircle>
                  </GameOverlay>
                </GameCard>
              ))
          }
        </GridWrapper>
      </div>
    );
  }
}

export default withRouter(GameByProvider);
